import { useMemo } from 'react';

/**
 * PriceTag — shared display for prices with optional discount.
 *
 * Props:
 *   price           : final price shown large (string like "AED 330" or "1,200 AED")
 *   compareAtPrice  : original price shown struck-through (same format, optional)
 *   size            : 'sm' | 'md' | 'lg' | 'xl'  (default 'md')
 *   align           : 'left' | 'right'           (default 'left')
 *   showBadge       : bool — show the "−XX%" pill  (default true)
 *
 * Discount % is computed automatically from the two numeric values inside the
 * strings (e.g. "AED 330" & "AED 400" → 18% OFF). Currency label is preserved
 * verbatim so we don't accidentally translate "AED" to something else.
 */

const parseNumber = (s) => {
  if (s == null) return null;
  const m = String(s).replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
  return m ? parseFloat(m[1]) : null;
};

const sizeCfg = {
  sm: { price: 'text-base font-semibold', compare: 'text-xs', badge: 'text-[10px] px-1.5 py-0.5' },
  md: { price: 'font-display text-xl font-semibold', compare: 'text-sm', badge: 'text-[10px] px-1.5 py-0.5' },
  lg: { price: 'font-display text-2xl font-semibold', compare: 'text-sm', badge: 'text-[11px] px-2 py-0.5' },
  xl: { price: 'font-display text-3xl sm:text-4xl font-semibold', compare: 'text-base', badge: 'text-xs px-2 py-1' },
};

export const PriceTag = ({
  price,
  compareAtPrice,
  size = 'md',
  align = 'left',
  showBadge = true,
  className = '',
  'data-testid': testId,
}) => {
  const cfg = sizeCfg[size] || sizeCfg.md;
  const priceNum = useMemo(() => parseNumber(price), [price]);
  const compareNum = useMemo(() => parseNumber(compareAtPrice), [compareAtPrice]);
  const hasDiscount = !!(price && compareAtPrice && priceNum != null && compareNum != null && compareNum > priceNum);
  const percentOff = hasDiscount ? Math.round(((compareNum - priceNum) / compareNum) * 100) : 0;

  if (!price) return null;

  const wrapClass = `inline-flex flex-wrap items-baseline gap-x-2 gap-y-0.5 ${align === 'right' ? 'justify-end' : 'justify-start'} ${className}`;

  return (
    <div className={wrapClass} data-testid={testId}>
      <span className={`${cfg.price} text-[#c8e600] leading-none`} data-testid={testId ? `${testId}-price` : undefined}>
        {price}
      </span>
      {hasDiscount && (
        <>
          <span
            className={`${cfg.compare} text-white/45 line-through decoration-white/40`}
            data-testid={testId ? `${testId}-compare` : undefined}
          >
            {compareAtPrice}
          </span>
          {showBadge && (
            <span
              className={`inline-flex items-center rounded-full bg-red-500/20 text-red-300 border border-red-400/30 font-bold tracking-wide ${cfg.badge}`}
              data-testid={testId ? `${testId}-badge` : undefined}
            >
              −{percentOff}% OFF
            </span>
          )}
        </>
      )}
    </div>
  );
};

export default PriceTag;
